import { AfterViewInit, ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, EventEmitter, Output, ViewChild, ViewEncapsulation } from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import { Store } from '@ngrx/store';
import { ExtendedPresence } from 'common/store/genesys-cloud/presence/models/extended-presence.model';
import { selectPresences } from 'common/store/genesys-cloud/presence/selectors/presence.selectors';
import { GenesysCloudQueue } from 'common/store/genesys-cloud/queues/models/genesys-cloud-queue.model';
import { GenesysCloudUser } from 'common/store/genesys-cloud/users/models/user.model';
import { combineLatestWith, Observable, Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, map, takeUntil } from 'rxjs/operators';
import { GenesysTransferPanelSearchStore } from './genesys-transfer-panel-search.store';
import { selectUser } from 'common/store/genesys-cloud/users/selectors/user.selectors';

@Component({
  selector: 'app-genesys-transfer-panel-search',
  templateUrl: './genesys-transfer-panel-search.component.html',
  styleUrls: ['./genesys-transfer-panel-search.component.scss'],
  providers: [GenesysTransferPanelSearchStore],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class GenesysTransferPanelSearchComponent implements AfterViewInit {

  private readonly onDestroySubject = new Subject<void>();

  readonly actualAgent$ = this.store.select(selectUser);
  readonly searchBox = new UntypedFormControl('');
  readonly users$ = this.searchStore.users$.pipe(
    combineLatestWith(this.actualAgent$),
    map(([users, agent]) => users.filter((user) => user.id !== agent.id)));
  readonly queues$ = this.searchStore.queues$;
  readonly systemPresences$: Observable<ExtendedPresence[]> = this.store.select(selectPresences);

  get searchValue(): string {
    return this.searchBox.value;
  }

  @Output()
  userSelected = new EventEmitter<GenesysCloudUser>();

  @Output()
  queueSelected = new EventEmitter<GenesysCloudQueue>();

  @ViewChild('searchInput', { static: true })
  searchInputElement: ElementRef;

  constructor(readonly changeDetectorRef: ChangeDetectorRef,
    private readonly searchStore: GenesysTransferPanelSearchStore,
    private readonly store: Store,
    public readonly elementRef: ElementRef) {

    this.searchBox.valueChanges.pipe(
        debounceTime(300),
        distinctUntilChanged(),
        takeUntil(this.onDestroySubject)
    ).subscribe(value => {
      this.searchStore.search(value);
      this.userSelected.emit(null);
      this.queueSelected.emit(null);
    });
  }

  readonly loading$ = this.searchStore.loading$;
  readonly canLoadMoreUsers$ = this.searchStore.canLoadMoreUsers$;
  readonly canLoadMoreQueues$ = this.searchStore.canLoadMoreQueues$;

  ngAfterViewInit(): void {
    this.searchInputElement.nativeElement.focus();
  }

  onLoadMoreUsers(): void {
    this.searchStore.loadAdditionalUsers();
  }

  onLoadMoreQueues(): void {
    this.searchStore.loadAdditionalQueues();
  }

  onUserSelected(user: GenesysCloudUser): void {
    this.userSelected.emit(user);
  }

  onQueueSelected(queue: GenesysCloudQueue): void {
    this.queueSelected.emit(queue);
  }
}
